import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import cx from "classnames";

import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { Icon } from "../../components/Icon/Icon";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { colors } from "../../styles/colors";
import useStyles from "./styles";
import {
  AGENT_CAPABILITIES,
  SUGGESTED_PROMPTS,
  AI_AGENT_CONFIG,
  loadConversations,
  persistConversations,
  makeConversation,
  deriveTitle,
  streamAgentReply,
  resetAgentSession,
  availableAgentModes,
  defaultAgentMode,
  AgentMode,
  ChatMessage,
  Conversation,
  ToolInvocation,
  Citation,
} from "../../api/aiAgent";
import { listPendingActions } from "../../api/aiCopilot";
import { ReportsPanel, ApprovalsPanel, MemoryPanel } from "./panels";
import { useAiPlanTier, AiPlanGating } from "../../hooks/useAiPlanTier";

/** Lightweight markdown-to-HTML for AI messages (no external dep). */
const renderMarkdown = (raw: string): string => {
  // Strip internal error/exception traces the LLM sometimes leaks
  let md = raw
    .replace(/\s*_Exception[^\n]*/g, "")
    .replace(/Thought:.*?Action:.*?(\n|$)/g, "")
    .replace(/^\s*\n/gm, "\n")
    .trim();
  if (!md) return "";
  // Escape HTML
  md = md.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Bold **text**
  md = md.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Italic *text* (but not list bullets)
  md = md.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
  // Inline code `text`
  md = md.replace(/`([^`]+)`/g, '<code style="background:rgba(37,99,235,0.08);padding:2px 5px;border-radius:4px;font-size:12.5px">$1</code>');
  // Unordered list items: lines starting with * or -
  md = md.replace(/^[*\-]\s+(.+)$/gm, '<li style="margin:2px 0;list-style:none;padding-left:16px;position:relative"><span style="position:absolute;left:0;color:#3B82F6">•</span>$1</li>');
  // Numbered list items: lines starting with 1. 2. etc
  md = md.replace(/^(\d+)\.\s+(.+)$/gm, '<li style="margin:2px 0;list-style:none;padding-left:20px;position:relative"><span style="position:absolute;left:0;color:#3B82F6;font-weight:500">$1.</span>$2</li>');
  // Headings ## and ###
  md = md.replace(/^###\s+(.+)$/gm, '<div style="font-weight:500;font-size:13.5px;margin:8px 0 4px">$1</div>');
  md = md.replace(/^##\s+(.+)$/gm, '<div style="font-weight:500;font-size:14.5px;margin:10px 0 4px">$1</div>');
  // Line breaks
  md = md.replace(/\n/g, "<br/>");
  return md;
};

/** Clean the displayed content of a message — hide LLM internal traces. */
const cleanContent = (content: string): string => {
  return content
    .replace(/\s*_Exception[^\n]*/g, "")
    .replace(/Thought:.*?Action:.*?(\n|$)/g, "")
    .replace(/^\s*\n/gm, "\n")
    .trim();
};

/** Local id helper (kept independent from the service layer). */
const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;

interface StreamingState {
  active: boolean;
  tools: ToolInvocation[];
  text: string;
}

const EMPTY_STREAM: StreamingState = { active: false, tools: [], text: "" };

type TabId = "chat" | "reports" | "approvals" | "memory";

/** Returns a tooltip message when a tab is locked, or null when accessible. */
const getTabLockMessage = (
  tabId: TabId,
  gating: AiPlanGating,
  t: (key: string, fallback: string) => string,
): string | null => {
  if (tabId === "chat") return null; // always accessible
  if (tabId === "reports" && gating.canReports) return null;
  if (tabId === "approvals" && gating.canApprovals) return null;
  if (tabId === "memory" && gating.canMemory) return null;

  // locked — decide the message based on current tier
  if (gating.tier === "ai_assistant") {
    return t(
      "aiAgent.upgrade.copilotOrAutonomous",
      "Vous avez besoin d'une mise à niveau vers Co Pilot ou Autonomous pour accéder à cette fonctionnalité",
    );
  }
  // If we reach here with copilot, all tabs are already accessible
  return t(
    "aiAgent.upgrade.autonomous",
    "Vous avez besoin d'une mise à niveau vers Autonomous pour accéder à cette fonctionnalité",
  );
};

export const AiAgent: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState<StreamingState>(EMPTY_STREAM);
  const [composerFocused, setComposerFocused] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("chat");
  const [pendingCount, setPendingCount] = useState(0);
  const [hoveredTab, setHoveredTab] = useState<TabId | null>(null);
  const aiGating = useAiPlanTier();

  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  // -- Bootstrap from local persistence ------------------------------------
  useEffect(() => {
    const loaded = loadConversations();
    setConversations(loaded);
    if (loaded.length) setActiveId(loaded[0].id);
  }, []);

  // Fetch pending-actions count once (for the badge on the Approvals tab).
  useEffect(() => {
    listPendingActions()
      .then((list) => setPendingCount(list.length))
      .catch(() => {});
  }, []);

  const active = conversations.find((c) => c.id === activeId) || null;
  const messages = active?.messages ?? [];
  const showHero = (!active || messages.length === 0) && !streaming.active;

  // -- Keep the thread pinned to the latest content ------------------------
  const threadRef = useRef<HTMLDivElement | null>(null);
  const userScrolledUp = useRef(false);
  const scrollRafId = useRef<number>(0);

  // Detect when user manually scrolls away from bottom
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userScrolledUp.current = distFromBottom > 100;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-scroll: use scrollTop directly (no smooth animation pile-up)
  // debounced via rAF so at most one scroll per frame.
  // Respects the user's reading position: once they scroll up, we never
  // yank them back down — sending a message or switching threads re-pins.
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    if (userScrolledUp.current) return;
    cancelAnimationFrame(scrollRafId.current);
    scrollRafId.current = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [messages.length, streaming.text, streaming.tools, activeId]);

  // Switching conversation always lands at the latest message.
  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    userScrolledUp.current = false;
    el.scrollTop = el.scrollHeight;
  }, [activeId]);

  // Reset composer height once it is cleared.
  useEffect(() => {
    if (input === "" && taRef.current) taRef.current.style.height = "auto";
  }, [input]);

  // -- Conversation management ---------------------------------------------
  const newChat = () => {
    if (streaming.active) return;
    const conv = makeConversation(t("aiAgent.newChat", "New chat"));
    const next = [conv, ...conversations];
    setConversations(next);
    setActiveId(conv.id);
    persistConversations(next);
    setActiveTab("chat");
    taRef.current?.focus();
  };

  const selectConversation = (id: string) => {
    if (streaming.active) return;
    setActiveId(id);
    setActiveTab("chat");
  };

  const deleteConversation = (id: string) => {
    const doomed = conversations.find((c) => c.id === id);
    if (doomed?.sessionId) resetAgentSession(doomed.sessionId);

    const next = conversations.filter((c) => c.id !== id);
    setConversations(next);
    persistConversations(next);
    if (activeId === id) setActiveId(next.length ? next[0].id : null);
  };

  const setAgentMode = (mode: AgentMode) => {
    if (streaming.active || !activeId) return;
    setConversations((prev) => {
      const next = prev.map((c) => (c.id === activeId ? { ...c, agentMode: mode } : c));
      persistConversations(next);
      return next;
    });
  };

  // -- Send a turn + stream the reply --------------------------------------
  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || streaming.active) return;

    // Switch to chat tab when sending a message
    setActiveTab("chat");

    let convId = activeId;
    let working = conversations;
    if (!convId) {
      const conv = makeConversation(deriveTitle(text));
      working = [conv, ...conversations];
      convId = conv.id;
    }

    const userMsg: ChatMessage = {
      id: newId(),
      role: "user",
      content: text,
      createdAt: Date.now(),
    };

    working = working.map((c) =>
      c.id === convId
        ? {
            ...c,
            title: c.messages.length === 0 ? deriveTitle(text) : c.title,
            messages: [...c.messages, userMsg],
            updatedAt: Date.now(),
          }
        : c
    );

    setConversations(working);
    setActiveId(convId);
    persistConversations(working);
    setInput("");
    // Sending re-pins the thread to the bottom so the user sees their message
    // and the incoming reply, even if they had scrolled up earlier.
    userScrolledUp.current = false;
    setStreaming({ active: true, tools: [], text: "" });

    const activeConv = working.find((c) => c.id === convId);
    const history = activeConv?.messages ?? [];
    const controller = new AbortController();
    abortRef.current = controller;

    await streamAgentReply(
      text,
      history,
      {
        signal: controller.signal,
        onToolUpdate: (tools) => setStreaming((s) => ({ ...s, tools })),
        onToken: (chunk) => setStreaming((s) => ({ ...s, text: s.text + chunk })),
        onSessionId: (sessionId) => {
          setConversations((prev) => {
            const next = prev.map((c) => (c.id === convId ? { ...c, sessionId } : c));
            persistConversations(next);
            return next;
          });
        },
        onDone: (assistantMsg) => {
          setConversations((prev) => {
            const next = prev.map((c) =>
              c.id === convId
                ? { ...c, messages: [...c.messages, assistantMsg], updatedAt: Date.now() }
                : c
            );
            persistConversations(next);
            return next;
          });
          setStreaming(EMPTY_STREAM);
          abortRef.current = null;
        },
        onError: () => {
          const errMsg: ChatMessage = {
            id: newId(),
            role: "assistant",
            content: t(
              "aiAgent.errorMessage",
              "Something went wrong while generating a response. Please try again."
            ),
            createdAt: Date.now(),
            error: true,
          };
          setConversations((prev) => {
            const next = prev.map((c) =>
              c.id === convId ? { ...c, messages: [...c.messages, errMsg] } : c
            );
            persistConversations(next);
            return next;
          });
          setStreaming(EMPTY_STREAM);
          abortRef.current = null;
        },
      },
      activeConv?.sessionId,
      activeConv?.agentMode ?? defaultAgentMode()
    );
  };

  const stop = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (streaming.text.trim() && activeId) {
      const partial: ChatMessage = {
        id: newId(),
        role: "assistant",
        content: streaming.text,
        createdAt: Date.now(),
        tools: streaming.tools.length ? streaming.tools : undefined,
      };
      setConversations((prev) => {
        const next = prev.map((c) =>
          c.id === activeId ? { ...c, messages: [...c.messages, partial] } : c
        );
        persistConversations(next);
        return next;
      });
    }
    setStreaming(EMPTY_STREAM);
  };

  // -- Composer behaviour --------------------------------------------------
  const onComposerKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !streaming.active) {
      e.preventDefault();
      send(input);
    }
  };

  const onComposerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  };

  // -- Small presentational helpers ----------------------------------------
  const TypingDots = () => (
    <span className={classes.typing} aria-hidden>
      <span className={classes.dot} style={{ animationDelay: "0ms" }} />
      <span className={classes.dot} style={{ animationDelay: "160ms" }} />
      <span className={classes.dot} style={{ animationDelay: "320ms" }} />
    </span>
  );

  const renderTrace = (tools: ToolInvocation[]) => {
    // Collapse consecutive identical steps. The backend loop guard now
    // prevents the runaway itself, but a legitimate retry (or an older
    // agent build) can still emit the same lookup twice, and eight
    // identical "get_expiring_documents · Aucun document..." rows read as
    // a crash rather than as one answered question.
    const grouped: { tool: ToolInvocation; count: number }[] = [];
    for (const tool of tools) {
      const prev = grouped[grouped.length - 1];
      if (
        prev &&
        prev.tool.name === tool.name &&
        prev.tool.status === tool.status &&
        (prev.tool.result ?? "") === (tool.result ?? "")
      ) {
        prev.count += 1;
      } else {
        grouped.push({ tool, count: 1 });
      }
    }

    return (
      <div className={classes.trace}>
        {grouped.map(({ tool, count }) => (
          <div className={classes.traceRow} key={tool.id}>
            {tool.status === "succeeded" ? (
              <Icon name="check" width={15} height={15} color={colors.successColor} />
            ) : tool.status === "failed" ? (
              <Icon name="close" width={15} height={15} color={colors.errorColor} />
            ) : (
              <TypingDots />
            )}
            <span>
              {tool.label}
              {count > 1 ? ` ×${count}` : ""}
              {tool.status === "succeeded" && tool.result ? ` · ${tool.result}` : ""}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderCitations = (citations: Citation[]) => (
    <div className={classes.citations}>
      {citations.map((c) => (
        <span
          key={c.id}
          className={classes.citation}
          onClick={() => c.href && navigate(c.href)}
        >
          <Icon name="external-link" width={13} height={13} color={colors.fleetBlue} />
          {c.title}
        </span>
      ))}
    </div>
  );

  const renderMessage = (msg: ChatMessage) => {
    const isUser = msg.role === "user";
    const cleaned = isUser ? msg.content : cleanContent(msg.content);
    // Skip messages that are entirely internal traces
    if (!cleaned && !isUser) return null;
    const html = isUser ? null : renderMarkdown(msg.content);
    return (
      <motion.div
        key={msg.id}
        className={cx(classes.row, isUser && classes.rowUser)}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
      >
        <div
          className={cx(
            classes.msgAvatar,
            isUser ? classes.msgAvatarUser : classes.msgAvatarAi
          )}
        >
          <Icon
            name={isUser ? "collaborateur" : "sparkles"}
            width={18}
            height={18}
            color={isUser ? colors.secondaryTextColor : "#F8FAFC"}
          />
        </div>

        <div className={cx(classes.msgWrap, isUser && classes.msgWrapUser)}>
          {!isUser && msg.tools && msg.tools.length > 0 && renderTrace(msg.tools)}
          <div
            className={cx(classes.bubble, isUser ? classes.bubbleUser : classes.bubbleAi)}
            style={msg.error ? { borderColor: "rgba(239,68,68,0.4)" } : undefined}
          >
            {isUser ? (
              cleaned
            ) : html ? (
              <span dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              cleaned
            )}
          </div>
          {!isUser && msg.citations && msg.citations.length > 0 && renderCitations(msg.citations)}
        </div>
      </motion.div>
    );
  };

  const renderStreamingRow = () => (
    <motion.div
      className={classes.row}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
    >
      <div className={cx(classes.msgAvatar, classes.msgAvatarAi)}>
        <Icon name="sparkles" width={18} height={18} color="#F8FAFC" />
      </div>
      <div className={classes.msgWrap}>
        {streaming.tools.length > 0 && renderTrace(streaming.tools)}
        <div className={cx(classes.bubble, classes.bubbleAi)}>
          {streaming.text ? (
            <span dangerouslySetInnerHTML={{ __html: renderMarkdown(streaming.text) }} />
          ) : (
            <TypingDots />
          )}
        </div>
      </div>
    </motion.div>
  );

  // -- Tab definitions -----------------------------------------------------
  const TABS: { id: TabId; label: string; icon: string }[] = [
    { id: "chat", label: t("aiAgent.tabs.chat", "Chat"), icon: "sparkles" },
    { id: "reports", label: t("aiAgent.tabs.reports", "Reports"), icon: "fileStack" },
    { id: "approvals", label: t("aiAgent.tabs.approvals", "Approvals"), icon: "stars" },
    { id: "memory", label: t("aiAgent.tabs.memory", "Memory"), icon: "collaborateur" },
  ];

  // -- Render --------------------------------------------------------------
  return (
    <Sidebar styles={{ sidebarRoot: classes.sidebarOuter, sidebarRightRoot: classes.root }}>
      <div className={classes.page}>
        {/* -- Conversation rail ------------------------------------------- */}
        <aside className={classes.convoRail}>
          <div className={classes.convoRailHeader}>
            <button className={classes.newChatBtn} onClick={newChat} type="button">
              <Icon name="plus" width={17} height={17} color="#F8FAFC" />
              {t("aiAgent.newChat", "New chat")}
            </button>
          </div>

          <div className={classes.convoList}>
            {conversations.length === 0 ? (
              <div
                style={{
                  padding: "14px 12px",
                  fontSize: 12.5,
                  color: colors.mutedText,
                  fontFamily: "Poppins-Regular",
                }}
              >
                {t("aiAgent.noConversations", "No conversations yet")}
              </div>
            ) : (
              conversations.map((c) => (
                <div
                  key={c.id}
                  role="button"
                  tabIndex={0}
                  className={cx(classes.convoItem, c.id === activeId && classes.convoItemActive)}
                  onClick={() => selectConversation(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectConversation(c.id);
                    }
                  }}
                >
                  <Icon name="sparkles" width={16} height={16} color={colors.secondaryTextColor} />
                  <span className={classes.convoTitle}>{c.title}</span>
                  <button
                    type="button"
                    className={classes.convoDelete}
                    aria-label={t("aiAgent.deleteConversation", "Delete conversation")}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(c.id);
                    }}
                  >
                    <Icon name="trash" width={15} height={15} color={colors.errorColor} />
                  </button>
                </div>
              ))
            )}
          </div>

          <div className={classes.railFooter}>
            <span className={classes.modelDot} />
            <span
              style={{
                fontSize: 12,
                color: colors.secondaryTextColor,
                fontFamily: "Poppins-Regular",
              }}
            >
              {AI_AGENT_CONFIG.model} · {t("aiAgent.online", "Online")}
            </span>
          </div>
        </aside>

        {/* -- Chat column ------------------------------------------------- */}
        <section className={classes.chatCol}>
          <header className={classes.chatHeader}>
            <div className={classes.avatar}>
              <Icon name="sparkles" width={20} height={20} color="#F8FAFC" />
            </div>
            <div>
              <div
                style={{
                  fontFamily: "Poppins-Medium",
                  fontSize: 15,
                  color: colors.primaryText,
                }}
              >
                {t("aiAgent.title", "Fleet Assistant")}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                <span className={classes.modelDot} />
                <span style={{ fontSize: 12, color: colors.secondaryTextColor }}>
                  {t("aiAgent.online", "Online")} · {AI_AGENT_CONFIG.model}
                </span>
              </div>
            </div>

            {/* Mode selector */}
            <div className={classes.modeSwitch}>
              {availableAgentModes().map((m) => (
                <button
                  key={m.id}
                  type="button"
                  disabled={streaming.active}
                  className={cx(
                    classes.modeChip,
                    (active?.agentMode ?? defaultAgentMode()) === m.id && classes.modeChipActive
                  )}
                  onClick={() => setAgentMode(m.id)}
                  title={t(`aiAgent.mode.${m.id}`, m.label)}
                >
                  {t(`aiAgent.mode.${m.id}`, m.label)}
                </button>
              ))}
            </div>
          </header>

          {/* -- Tab bar --------------------------------------------------- */}
          <div className={classes.tabBar}>
            {TABS.map((tab) => {
              const lockMsg = getTabLockMessage(tab.id, aiGating, t);
              const isLocked = lockMsg !== null;
              return (
                <div
                  key={tab.id}
                  style={{ position: "relative", display: "inline-flex" }}
                  onMouseEnter={() => isLocked && setHoveredTab(tab.id)}
                  onMouseLeave={() => setHoveredTab(null)}
                >
                  <button
                    type="button"
                    className={cx(
                      classes.tab,
                      activeTab === tab.id && !isLocked && classes.tabActive,
                      isLocked && classes.tabLocked,
                    )}
                    onClick={() => !isLocked && setActiveTab(tab.id)}
                    style={isLocked ? { cursor: "default" } : undefined}
                  >
                    <Icon
                      name={isLocked ? "lock" : tab.icon}
                      width={16}
                      height={16}
                      color={
                        isLocked
                          ? colors.mutedText
                          : activeTab === tab.id
                            ? colors.fleetBlue
                            : colors.secondaryTextColor
                      }
                    />
                    {tab.label}
                    {tab.id === "approvals" && pendingCount > 0 && !isLocked && (
                      <span className={classes.tabBadge}>{pendingCount}</span>
                    )}
                  </button>
                  {isLocked && hoveredTab === tab.id && (
                    <div className={classes.tabTooltip}>
                      {lockMsg}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* -- Tab content ------------------------------------------------ */}
          {activeTab === "reports" ? (
            <div className={classes.thread}><ReportsPanel /></div>
          ) : activeTab === "approvals" ? (
            <div className={classes.thread}><ApprovalsPanel /></div>
          ) : activeTab === "memory" ? (
            <div className={classes.thread}><MemoryPanel /></div>
          ) : (
            /* Chat tab */
            <>
              <div className={classes.thread} ref={threadRef}>
                {showHero ? (
                  <div className={classes.hero}>
                    <motion.div
                      className={classes.heroBadge}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Icon name="sparkles" width={30} height={30} color="#FFFFFF" />
                    </motion.div>

                    <CustomTypography
                      variant="h2"
                      text={t("aiAgent.emptyTitle", "How can I help with your fleet today?")}
                      textAlign="center"
                    />
                    <p
                      style={{
                        maxWidth: 520,
                        margin: 0,
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: colors.secondaryTextColor,
                        fontFamily: "Poppins-Regular",
                      }}
                    >
                      {t(
                        "aiAgent.emptySubtitle",
                        "Ask about vehicles, costs, documents or claims — I can analyze your data and help you take action."
                      )}
                    </p>

                    <div className={classes.capGrid}>
                      {AGENT_CAPABILITIES.map((cap) => (
                        <div className={classes.capCard} key={cap.id}>
                          <div className={classes.capIcon} style={{ color: colors.fleetBlue }}>
                            <Icon name={cap.icon} width={20} height={20} color={colors.fleetBlue} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div
                              style={{
                                fontFamily: "Poppins-Medium",
                                fontSize: 14,
                                color: colors.primaryText,
                                marginBottom: 3,
                              }}
                            >
                              {t(cap.titleKey, cap.titleDefault)}
                            </div>
                            <div
                              style={{
                                fontSize: 12.5,
                                lineHeight: 1.5,
                                color: colors.secondaryTextColor,
                                fontFamily: "Poppins-Regular",
                              }}
                            >
                              {t(cap.descriptionKey, cap.descriptionDefault)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className={classes.suggestRow}>
                      {SUGGESTED_PROMPTS.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className={classes.suggestChip}
                          onClick={() => send(t(p.promptKey, p.promptDefault))}
                        >
                          <Icon name={p.icon} width={16} height={16} color={colors.fleetBlue} />
                          {t(p.labelKey, p.labelDefault)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className={classes.threadInner}>
                    <AnimatePresence initial={false}>
                      {messages.map((m) => renderMessage(m))}
                    </AnimatePresence>
                    {streaming.active && renderStreamingRow()}
                    <div ref={bottomRef} />
                  </div>
                )}
              </div>

              {/* -- Composer (chat tab only) -------------------------------- */}
              <div className={classes.composerWrap}>
                <div className={cx(classes.composer, composerFocused && classes.composerFocused)}>
                  <textarea
                    ref={taRef}
                    className={classes.input}
                    value={input}
                    rows={1}
                    placeholder={t("aiAgent.placeholder", "Ask anything about your fleet…")}
                    aria-label={t("aiAgent.placeholder", "Ask anything about your fleet…")}
                    onFocus={() => setComposerFocused(true)}
                    onBlur={() => setComposerFocused(false)}
                    onChange={onComposerChange}
                    onKeyDown={onComposerKeyDown}
                  />
                  {streaming.active ? (
                    <button
                      type="button"
                      className={classes.sendBtn}
                      onClick={stop}
                      title={t("aiAgent.stop", "Stop")}
                      aria-label={t("aiAgent.stop", "Stop")}
                    >
                      <Icon name="stop" width={16} height={16} color="#F8FAFC" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={cx(classes.sendBtn, !input.trim() && classes.sendBtnDisabled)}
                      disabled={!input.trim()}
                      onClick={() => send(input)}
                      title={t("aiAgent.send", "Send")}
                      aria-label={t("aiAgent.send", "Send")}
                    >
                      <Icon name="send" width={18} height={18} />
                    </button>
                  )}
                </div>
                <div className={classes.disclaimer}>
                  {t(
                    "aiAgent.disclaimer",
                    "AI-generated responses may contain inaccuracies. Always verify critical fleet decisions."
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </Sidebar>
  );
};

export default AiAgent;
